import Image from 'next/image'
import Link from '@/components/ui/Link'
import { getReadableImageUrl } from '@/lib/directus'
import { sanitizeHTML } from '@/lib/utils'
import { getBlogPostUrl } from '@/lib/blog'
import { htmlToPlainText, createExcerpt } from '@/components/Blog/utils'

interface SecReadMoreAboutProps {
	data: {
		id: number
		blog_id_post?: string // Строка с ID постов, разделенными запятыми (например: "10,17,20")
		heading?: {
			title?: string
		}
		content_collection?: {
			id: number
			site_content_collections_id: {
				title: string
				collection: {
					item: {
						text?: string // from site_content.text
						title?: string // from external_links.title
						url?: string // from external_links.url
						image?: { // from site_content.image
							id: string
							title: string
							filename_disk: string
							filename_download: string
						}
					}
				}[]
			}
		}[]
		// Добавляем посты блога
		blogPosts?: {
			id: string
			title: string
			slug: string
			content: string
			published_at: string
			featured_image?: {
				id: string
				filename_disk: string
			}
			author?: {
				slug: string
				title: string
			}
		}[]
	}
}

export const fields = [
	'blog_id_post',
	'heading.title',

	'content_collection.id',
	'content_collection.site_content_collections_id.collection.item:site_content.title',
	'content_collection.site_content_collections_id.collection.item:site_content.text',

	'content_collection.site_content_collections_id.collection.item:site_content.image.id',
	'content_collection.site_content_collections_id.collection.item:site_content.image.title',
	'content_collection.site_content_collections_id.collection.item:site_content.image.filename_disk',
	'content_collection.site_content_collections_id.collection.item:site_content.image.filename_download',

	'content_collection.site_content_collections_id.collection.item:external_links.title',
	'content_collection.site_content_collections_id.collection.item:external_links.url',
] as const

export default function SecReadMoreAbout({ data }: SecReadMoreAboutProps) {

	// Объединяем обычные элементы контента и посты блога
	const allItems = [
		// Элементы из content_collection
		...(data?.content_collection?.map((item) => ({
			id: item.id,
			type: 'content' as const,
			data: item
		})) || []),
		// Посты блога
		...(data?.blogPosts?.map((post) => ({
			id: post.id,
			type: 'blog' as const,
			data: post
		})) || [])
	]

	return (
		<section className='SecReadMoreAbout w-full bg-[#bec3b7] py-16'>
			<div className='container'>
				<h2 className='mb-12'>{data?.heading?.title || ''}</h2>

				<div className='grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-6 2xl:gap-12'>
					{allItems.map((item) => {
						if (item.type === 'content') {
							// Обработка обычного контента
							const contentItem = item.data as NonNullable<SecReadMoreAboutProps['data']['content_collection']>[number]
							const image = contentItem.site_content_collections_id.collection.find(
								c => 'image' in c.item && c.item.image !== null
							)?.item?.image
							const titleItem = contentItem.site_content_collections_id.collection.find(c => c.item.title)?.item
							const textItem = contentItem.site_content_collections_id.collection.find(c => c.item.text)?.item
							const externalLink = contentItem.site_content_collections_id.collection.find(
								c => 'url' in c.item
							)?.item

							return (
								<div key={item.id} className='flex flex-col gap-6 lg:gap-8'>
									{image && (
										<div className='relative w-full h-[16.5rem] lg:h-[13.5rem] 2xl:h-[17.5rem] 3xl:h-[22.5rem] rounded-lg overflow-hidden'>
											<Image
												src={getReadableImageUrl(
													image.filename_disk,
													image.filename_download
												)}
												alt={image.title || ''}
												fill
												className='object-cover'
												sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
											/>
										</div>
									)}
									<h3 className='text-3xl 3xl:text-4xl uppercase'>
										{titleItem?.title || contentItem.site_content_collections_id.title || ''}
									</h3>
									<div
										dangerouslySetInnerHTML={sanitizeHTML(textItem?.text || '')}
									/>
									{externalLink?.url && (
										<a
											href={externalLink.url}
											className='px-9 py-3 bg-brown rounded-lg text-white text-2xl font-brandon hover:bg-brown-light transition-colors w-full md:w-fit text-center'
										>
											{externalLink.title || 'Read the full article'}
										</a>
									)}
								</div>
							)
						} else {
							// Обработка постов блога
							const blogPost = item.data as NonNullable<SecReadMoreAboutProps['data']['blogPosts']>[number]
							
							return (
								<div key={item.id} className='flex flex-col gap-6 lg:gap-8'>
									{blogPost.featured_image && (
										<div className='relative w-full h-[16.5rem] lg:h-[13.5rem] 2xl:h-[17.5rem] 3xl:h-[22.5rem] rounded-lg overflow-hidden'>
											<Image
												src={getReadableImageUrl(
													blogPost.featured_image.id,
													blogPost.featured_image.filename_disk
												)}
												alt={htmlToPlainText(blogPost.title) || ''}
												fill
												className='object-cover'
												sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
											/>
										</div>
									)}
								<h3 className='text-3xl 3xl:text-4xl uppercase'>
									{htmlToPlainText(blogPost.title)}
								</h3>
								<div className='line-clamp-3'>
									<p>{createExcerpt(blogPost.content, 150)}</p>
								</div>
									<Link
										href={getBlogPostUrl(blogPost.slug)}
										className='px-9 py-3 bg-brown rounded-lg text-white text-2xl font-brandon hover:bg-brown-light transition-colors w-full md:w-fit text-center'
									>
										Read the full article
									</Link>
								</div>
							)
						}
					})}
				</div>
			</div>
		</section>
	)
}